import { KEYPAD_ISSUE, advisory, blocking } from '../catalog/issue';
import type { KeypadIssue } from '../catalog/issue';
import {
  KEYPAD_ADDRESS_MODE,
  KEYPAD_FAMILY_SPECS,
  addressGroupOf,
  isAddressInRange,
  isSingleInstanceFamily,
} from '../catalog/family';
import type { KeypadFamilyKey } from '../catalog/family';
import { KEYPAD_LIMITS } from '../catalog/limits';
import { KEYPAD_TYPE_SPECS, familyOfType, keyCountOfType } from '../catalog/type';
import type { KeypadTypeKey } from '../catalog/type';
import { newKeypadKeyId } from '../ids';
import type { KeypadId, KeypadKeyId } from '../ids';
import { createKeypad, familyOf, withKey } from '../model';
import type { Keypad, KeypadBook, KeypadDraft, KeypadKey } from '../model';
import { fail, ok } from '../result';
import type { KeypadResult } from '../result';

/**
 * Edits to the panels themselves: adding one, moving it, renaming its keys.
 *
 * Every command is pure — it returns a new panel or an issue, never a
 * half-applied change — so the store commits only what succeeded. Nothing here
 * touches the device: what is written to the RCU is `encode`'s business.
 */

/** A name a panel can be listed under, or the reason there is none. */
const cleanPanelName = (name: string): KeypadResult<string> => {
  const trimmed = name.trim();
  if (trimmed === '') return fail(blocking(KEYPAD_ISSUE.NAME_REQUIRED, 'A panel needs a name.'));
  if (trimmed.length > KEYPAD_LIMITS.NAME_MAX) {
    return fail(
      blocking(
        KEYPAD_ISSUE.NAME_TOO_LONG,
        `Keep the name to ${KEYPAD_LIMITS.NAME_MAX} characters so it fits in the list.`,
      ),
    );
  }
  return ok(trimmed);
};

/** Panels of the same family already in the book, ignoring one being edited. */
const familyMates = (
  book: KeypadBook,
  family: KeypadFamilyKey,
  ignoreId: KeypadId | null,
): readonly Keypad[] =>
  book.keypads.filter((keypad) => familyOf(keypad) === family && keypad.id !== ignoreId);

/**
 * Panels drawing on one address group, ignoring one being edited.
 *
 * Addresses are unique per *group*, not per family: a keypad and a logic board
 * both answer in group 3, so they cannot share a number, while a corridor panel's
 * group-2 keys and a keycard reader's group-4 key are out of the way.
 */
const groupMates = (
  book: KeypadBook,
  group: number,
  ignoreId: KeypadId | null,
): readonly Keypad[] =>
  book.keypads.filter(
    (keypad) => keypad.id !== ignoreId && addressGroupFor(keypad) === group,
  );

/**
 * The family's address rules applied to a panel that is being added or edited.
 *
 * A selectable family needs an address in its group that no other panel of the
 * same family uses. A fixed family must not carry one at all — its keys bring
 * their own — and because those addresses are shared, such a family can only be
 * placed once per RCU.
 */
const addressIssues = (
  book: KeypadBook,
  type: KeypadTypeKey,
  address: number | null,
  ignoreId: KeypadId | null,
): readonly KeypadIssue[] => {
  const family = familyOfType(type);
  const spec = KEYPAD_FAMILY_SPECS[family];
  const issues: KeypadIssue[] = [];

  if (isSingleInstanceFamily(family) && familyMates(book, family, ignoreId).length > 0) {
    issues.push(
      blocking(
        KEYPAD_ISSUE.FAMILY_ALREADY_PRESENT,
        `Only one ${spec.label.toLowerCase()} can answer on this RCU: its keys use fixed addresses.`,
      ),
    );
  }

  if (spec.addressMode !== KEYPAD_ADDRESS_MODE.SELECTABLE) return issues;

  if (address === null) {
    return [
      ...issues,
      blocking(
        KEYPAD_ISSUE.ADDRESS_NOT_SET,
        `A ${spec.label.toLowerCase()} needs an address in group ${spec.group}.`,
      ),
    ];
  }
  if (!isAddressInRange(address)) {
    return [
      ...issues,
      blocking(
        KEYPAD_ISSUE.ADDRESS_OUT_OF_RANGE,
        `A panel's address is ${KEYPAD_LIMITS.ADDRESS_MIN}–${KEYPAD_LIMITS.ADDRESS_MAX}.`,
      ),
    ];
  }

  const clash = groupMates(book, spec.group, ignoreId).find(
    (keypad) => keypad.address === address,
  );
  if (clash === undefined) return issues;

  return [
    ...issues,
    blocking(
      KEYPAD_ISSUE.ADDRESS_TAKEN,
      `“${clash.name}” already answers at address ${address} in group ${spec.group}.`,
    ),
  ];
};

/** The address a panel is stored with: a fixed family keeps none. */
const storedAddress = (type: KeypadTypeKey, address: number | null): number | null =>
  KEYPAD_FAMILY_SPECS[familyOfType(type)].addressMode === KEYPAD_ADDRESS_MODE.SELECTABLE
    ? address
    : null;

/** The keys a panel keeps when its type changes. */
const retypeKeys = (keypad: Keypad, type: KeypadTypeKey): readonly KeypadKey[] =>
  Array.from({ length: keyCountOfType(type) }, (_unused, index) => {
    // Same index means the same key, so its name and what it drives survive the
    // change; a key the new layout does not have simply goes away.
    const existing = keypad.keys.find((key) => key.index === index);
    if (existing !== undefined) return existing;
    return {
      id: newKeypadKeyId(),
      index,
      name: KEYPAD_TYPE_SPECS[type].defaultKeyNames[index] ?? '',
      states: [],
    };
  });

/** A new panel, once its name and address hold up. */
export const addKeypad = (
  book: KeypadBook,
  draft: KeypadDraft,
  now: string,
): KeypadResult<Keypad> => {
  const name = cleanPanelName(draft.name);
  if (!name.ok) return name;

  const address = storedAddress(draft.type, draft.address);
  const issues = addressIssues(book, draft.type, address, null);
  const first = issues[0];
  if (first !== undefined) return fail(first);

  return ok(createKeypad({ ...draft, name: name.value, address }, now));
};

/** What an edit may change. Keys are edited through their own commands. */
export interface KeypadPatch {
  readonly name?: string;
  readonly type?: KeypadTypeKey;
  readonly address?: number | null;
  readonly cat6Branch?: number | null;
  readonly location?: string;
}

/**
 * Applies an edit. Moving a selectable panel remembers the address it leaves, so
 * the console can later offer to erase what the device still holds there.
 */
export const updateKeypad = (
  book: KeypadBook,
  keypadId: KeypadId,
  patch: KeypadPatch,
): KeypadResult<Keypad> => {
  const existing = findKeypad(book, keypadId);
  if (existing === undefined) return fail(panelGone());

  const type = patch.type ?? existing.type;
  const name = patch.name === undefined ? ok(existing.name) : cleanPanelName(patch.name);
  if (!name.ok) return name;

  const address = storedAddress(
    type,
    patch.address === undefined ? existing.address : patch.address,
  );
  const issues = addressIssues(book, type, address, keypadId);
  const first = issues[0];
  if (first !== undefined) return fail(first);

  const moved =
    existing.address !== null && address !== null && existing.address !== address
      ? { retiredAddresses: [...existing.retiredAddresses, existing.address] }
      : {};

  return ok({
    ...existing,
    type,
    name: name.value,
    address,
    cat6Branch: patch.cat6Branch === undefined ? existing.cat6Branch : patch.cat6Branch,
    location: patch.location === undefined ? existing.location : patch.location,
    keys: type === existing.type ? existing.keys : retypeKeys(existing, type),
    ...moved,
  });
};

/** Renames the panel itself, without touching its keys. */
export const renameKeypad = (
  book: KeypadBook,
  keypadId: KeypadId,
  name: string,
): KeypadResult<Keypad> => updateKeypad(book, keypadId, { name });

/** Moves a selectable panel to another address in its group. */
export const setKeypadAddress = (
  book: KeypadBook,
  keypadId: KeypadId,
  address: number,
): KeypadResult<Keypad> => updateKeypad(book, keypadId, { address });

/** Forgets an address a panel no longer uses, once its mappings are gone. */
export const forgetRetiredAddress = (
  book: KeypadBook,
  keypadId: KeypadId,
  address: number,
): KeypadResult<Keypad> => {
  const existing = findKeypad(book, keypadId);
  if (existing === undefined) return fail(panelGone());
  return ok({
    ...existing,
    retiredAddresses: existing.retiredAddresses.filter((entry) => entry !== address),
  });
};

/** Renames one key. An empty name falls back to the type's own label. */
export const renameKey = (
  book: KeypadBook,
  keypadId: KeypadId,
  keyId: KeypadKeyId,
  name: string,
): KeypadResult<Keypad> => {
  const trimmed = name.trim();
  if (trimmed.length > KEYPAD_LIMITS.KEY_NAME_MAX) {
    return fail(
      blocking(
        KEYPAD_ISSUE.NAME_TOO_LONG,
        `Keep an engraving label to ${KEYPAD_LIMITS.KEY_NAME_MAX} characters.`,
      ),
    );
  }
  return editKey(book, keypadId, keyId, (key) => ({ ...key, name: trimmed }));
};

/** Removes a panel. What the device still holds is the caller's to erase. */
export const removeKeypad = (book: KeypadBook, keypadId: KeypadId): KeypadResult<Keypad> => {
  const existing = findKeypad(book, keypadId);
  return existing === undefined ? fail(panelGone()) : ok(existing);
};

/** The group a panel's address comes out of, or null when its keys are fixed. */
export const addressGroupFor = (keypad: Keypad): number | null => addressGroupOf(familyOf(keypad));

/**
 * Addresses the other panels of this panel's group already answer on.
 *
 * Uniqueness is per *group*, not per family: a standard keypad and a logic board
 * both draw on group 3, so they cannot share a number. Used by the editor to say
 * what is in use and to offer the next free one.
 */
export const takenAddresses = (
  book: KeypadBook,
  family: KeypadFamilyKey,
  ignoreId: KeypadId | null,
): readonly number[] => {
  const group = addressGroupOf(family);
  if (group === null) return [];
  return groupMates(book, group, ignoreId).flatMap((other) =>
    other.address === null ? [] : [other.address],
  );
};

/** Advisory notes about a panel, before any of its states are considered. */
export const panelAdvisories = (book: KeypadBook, keypad: Keypad): readonly KeypadIssue[] => {
  const issues: KeypadIssue[] = [];
  const expected = keyCountOfType(keypad.type);
  if (keypad.keys.length !== expected) {
    issues.push(
      advisory(
        KEYPAD_ISSUE.KEY_COUNT_MISMATCH,
        `${KEYPAD_TYPE_SPECS[keypad.type].label} should have ${expected} keys.`,
      ),
    );
  }
  if (book.keypads.some((other) => other.id !== keypad.id && other.name === keypad.name)) {
    issues.push(
      advisory(KEYPAD_ISSUE.DUPLICATE_NAME, 'Another panel in the book has the same name.'),
    );
  }
  return issues;
};

const findKeypad = (book: KeypadBook, keypadId: KeypadId): Keypad | undefined =>
  book.keypads.find((keypad) => keypad.id === keypadId);

const panelGone = (): KeypadIssue =>
  blocking(KEYPAD_ISSUE.KEYPAD_NOT_FOUND, 'That panel is no longer in the book.');

/** Applies an edit to one key, or refuses when the key is not on the panel. */
const editKey = (
  book: KeypadBook,
  keypadId: KeypadId,
  keyId: KeypadKeyId,
  update: (key: KeypadKey) => KeypadKey,
): KeypadResult<Keypad> => {
  const existing = findKeypad(book, keypadId);
  if (existing === undefined) return fail(panelGone());
  if (!existing.keys.some((key) => key.id === keyId)) {
    return fail(blocking(KEYPAD_ISSUE.KEY_NOT_FOUND, 'That button is not on this panel.'));
  }
  return ok(withKey(existing, keyId, update));
};
